import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scissors, PaintBrush, Stethoscope, DeviceMobile, Storefront,
  CalendarCheck, Clock, CheckCircle, ArrowUpRight,
} from "@phosphor-icons/react";
import { SiteTopBar } from "../components/site/SiteTopBar";
import { Footer } from "../components/site/Footer";
import { AppBadges } from "../components/site/AppBadges";
import { fadeUp, stagger, viewport } from "../lib/motion";
import { ADMIN_URL } from "../lib/site";

const DATA = {
  "salao-de-beleza": {
    name: "Salão de Beleza",
    icon: Scissors,
    accent: "text-maraca-pink",
    tagline: "Agende cortes, escova, coloração e tratamentos escolhendo o melhor horário — sem filas e sem ligações.",
    examples: ["Corte e escova", "Coloração e luzes", "Tratamentos capilares", "Penteados para eventos"],
  },
  manicure: {
    name: "Manicure",
    icon: PaintBrush,
    accent: "text-maraca-amber",
    tagline: "Marque manicure, pedicure e alongamento no horário que for melhor pra você, direto pelo app.",
    examples: ["Manicure e pedicure", "Alongamento de unhas", "Esmaltação em gel", "Spa dos pés"],
  },
  clinica: {
    name: "Clínica",
    icon: Stethoscope,
    accent: "text-maraca-teal",
    tagline: "Agende consultas e atendimentos de saúde com praticidade, inclusive no conforto da sua casa.",
    examples: ["Consultas", "Atendimento em casa", "Exames e curativos", "Acompanhamento"],
  },
};

const clienteSteps = [
  { icon: DeviceMobile, title: "Escolha o serviço", desc: "Abra o app, selecione a categoria e o profissional ou estabelecimento que você quer." },
  { icon: Clock, title: "Veja os horários livres", desc: "O app mostra em tempo real apenas os horários disponíveis — os já reservados ficam bloqueados." },
  { icon: CalendarCheck, title: "Reserve pelo app", desc: "Toque no horário desejado e confirme a reserva em segundos, sem precisar ligar." },
  { icon: CheckCircle, title: "Receba a confirmação", desc: "Você recebe a confirmação na hora e um lembrete antes do seu atendimento." },
];

const lojistaSteps = [
  { icon: Storefront, title: "Cadastre seus serviços", desc: "Monte a lista de serviços com duração e preço no Painel do Lojista." },
  { icon: Clock, title: "Defina seus horários livres", desc: "Configure os dias e horários em que você atende e libera para reserva." },
  { icon: CalendarCheck, title: "Bloqueie horários reservados", desc: "Marque como reservado o que já está ocupado — some automaticamente para o cliente." },
  { icon: CheckCircle, title: "Gerencie sua agenda", desc: "Acompanhe, confirme ou remarque as reservas recebidas direto pelo painel." },
];

const StepCard = ({ step, color }) => (
  <motion.div variants={fadeUp} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-white p-5">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-50">
      <step.icon size={24} weight="duotone" className={color} />
    </span>
    <div>
      <h4 className="font-display text-lg font-bold text-maraca-navy">{step.title}</h4>
      <p className="mt-1 text-sm leading-relaxed text-maraca-navy/70">{step.desc}</p>
    </div>
  </motion.div>
);

export default function Agendamento() {
  const { slug } = useParams();
  const data = DATA[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <SiteTopBar />

      <section className="relative overflow-hidden bg-white pt-32 md:pt-40" data-testid="agendamento-hero">
        <div className="pointer-events-none absolute -right-32 top-10 h-80 w-80 rounded-full bg-maraca-teal/15 blur-3xl" />
        <div className="mx-auto max-w-8xl px-5 md:px-10">
          <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-maraca-pink">
            Agendamento pelo app
          </p>
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50">
              <data.icon size={36} weight="duotone" className={data.accent} />
            </span>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-maraca-navy md:text-5xl lg:text-6xl">
              {data.name}
            </h1>
          </div>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-maraca-navy/70">{data.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {data.examples.map((e) => (
              <span key={e} className="rounded-full border border-black/10 bg-slate-50 px-4 py-1.5 text-sm font-semibold text-maraca-navy">
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-8xl gap-12 px-5 md:grid-cols-2 md:px-10">
          <div>
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-maraca-teal/10 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest text-maraca-navy">
                <DeviceMobile size={16} weight="fill" className="text-maraca-teal" /> Para o cliente
              </span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-maraca-navy md:text-4xl">
                Reserve seu horário em minutos
              </h2>
            </div>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="space-y-4">
              {clienteSteps.map((s) => <StepCard key={s.title} step={s} color="text-maraca-teal" />)}
            </motion.div>
            <div className="mt-8">
              <AppBadges />
            </div>
          </div>

          <div>
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-maraca-amber/15 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest text-maraca-navy">
                <Storefront size={16} weight="fill" className="text-maraca-amber" /> Para o lojista
              </span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-maraca-navy md:text-4xl">
                Controle total da sua agenda
              </h2>
            </div>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="space-y-4">
              {lojistaSteps.map((s) => <StepCard key={s.title} step={s} color="text-maraca-amber" />)}
            </motion.div>
            <a
              href={ADMIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agendamento-lojista-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-maraca-navy px-6 py-3.5 font-display text-base font-bold text-white transition-transform duration-300 hover:-translate-y-1"
            >
              Cadastre sua loja
              <ArrowUpRight size={20} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-maraca-navy py-16 text-center text-white">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
            Baixe o app e agende agora
          </h2>
          <p className="mt-3 text-white/70">Serviços de {data.name.toLowerCase()} e muito mais na palma da sua mão.</p>
          <AppBadges className="mt-6 justify-center" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
